import React, { useState, useMemo } from 'react';
import { CartItem } from '../../types/index';
import { calculateModificationDifference, modifyOrder } from '../../services/orderModification.service';
import { Button } from '../common/Button';
import { toast } from 'react-hot-toast';

interface OrderModificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  userId: string;
  originalItems: CartItem[];
  originalTotal: number;
  onSuccess: () => void;
}

export const OrderModificationModal: React.FC<OrderModificationModalProps> = ({
  isOpen,
  onClose,
  orderId,
  userId,
  originalItems,
  originalTotal,
  onSuccess
}) => {
  const [modifiedItems, setModifiedItems] = useState<CartItem[]>([...originalItems]);
  const [cancelledItemIds, setCancelledItemIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'wallet' | 'upi' | 'food'>('wallet');
  
  // ✅ ADD NEW ITEM STATES - INSIDE COMPONENT
  const [newItem, setNewItem] = useState<{ name: string; price: number; quantity: number } | null>(null);
  const [showAddItem, setShowAddItem] = useState(false);

  // Calculate differences in real-time
  const calculation = useMemo(() => {
    return calculateModificationDifference(originalItems, modifiedItems, cancelledItemIds);
  }, [originalItems, modifiedItems, cancelledItemIds]);

  const newTotal = originalTotal + calculation.difference;

  // Handle quantity change
  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    
    setModifiedItems(prev => 
      prev.map(item => 
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  // Handle item cancellation
  const toggleCancelItem = (itemId: string) => {
    setCancelledItemIds(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // ✅ HANDLE ADD NEW ITEM
 const handleAddNewItem = () => {
  if (!newItem || !newItem.name || newItem.price <= 0 || newItem.quantity < 1) {
    toast.error("Please fill in all fields correctly");
    return;
  }

  // ✅ Extract kitchen from original items (assumes single kitchen per order)
  const kitchenId = originalItems[0]?.kitchenId || 'admin_default_kitchen';
  const kitchenName = originalItems[0]?.kitchenName || 'Admin Added';

  const newCartItem: CartItem = {
    id: `new_${Date.now()}`,
    name: newItem.name,
    price: newItem.price,
    quantity: newItem.quantity,
    category: "added",
    description: "Added by admin",
    kitchenId,  // ✅ Now included
    kitchenName  // ✅ Now included
  };

  setModifiedItems([...modifiedItems, newCartItem]);
  setNewItem(null);
  setShowAddItem(false);
  toast.success("Item added to order");
};


  // Submit modification
  const handleSubmit = async () => {
    if (cancelledItemIds.length === originalItems.length) {
      toast.error("Cannot cancel all items. Cancel the entire order instead.");
      return;
    }

    if (calculation.difference === 0) {
      toast.error("No changes detected");
      return;
    }

    setLoading(true);
    try {
      const result = await modifyOrder(
        orderId,
        userId,
        modifiedItems,
        cancelledItemIds,
        calculation.paymentRequired ? paymentMethod : undefined
      );

      if (result.success) {
        toast.success(result.message);
        onSuccess();
        onClose();
      } else {
        toast.error(result.message);
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to modify order");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-orange-50 p-6 border-b border-orange-100">
          <h2 className="text-2xl font-bold text-gray-800">Modify Order</h2>
          <p className="text-sm text-gray-600 mt-1">Cancel items, adjust quantities, or add new items</p>
        </div>

        {/* Body - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-4">
            {/* EXISTING ITEMS */}
            {modifiedItems.filter(item => !item.id.startsWith('new_')).map((item) => {
              const isCancelled = cancelledItemIds.includes(item.id);
              const currentQuantity = item.quantity;

              return (
                <div 
                  key={item.id}
                  className={`border-2 rounded-lg p-4 transition-all ${
                    isCancelled 
                      ? 'border-red-300 bg-red-50 opacity-60' 
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Item Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isCancelled}
                          onChange={() => toggleCancelItem(item.id)}
                          className="w-5 h-5 text-red-600 rounded"
                        />
                        <div>
                          <h3 className={`font-bold ${isCancelled ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                            {item.name}
                          </h3>
                          <p className="text-sm text-gray-600">₹{item.price.toFixed(2)} each</p>
                        </div>
                      </div>
                    </div>

                    {/* Quantity Controls */}
                    {!isCancelled && (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleQuantityChange(item.id, currentQuantity - 1)}
                          disabled={currentQuantity <= 1}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                        >
                          −
                        </button>
                        <span className="font-bold text-lg w-8 text-center">{currentQuantity}</span>
                        <button
                          onClick={() => handleQuantityChange(item.id, currentQuantity + 1)}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold"
                        >
                          +
                        </button>
                      </div>
                    )}

                    {/* Price */}
                    <div className="text-right">
                      <p className={`font-bold ${isCancelled ? 'line-through text-gray-500' : 'text-orange-600'}`}>
                        ₹{(item.price * currentQuantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* NEWLY ADDED ITEMS */}
            {modifiedItems.filter(item => item.id.startsWith('new_')).map((item) => (
              <div 
                key={item.id}
                className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-bold text-blue-800">✨ {item.name}</h3>
                    <p className="text-sm text-blue-600">₹{item.price.toFixed(2)} each (Added by admin)</p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                    >
                      −
                    </button>
                    <span className="font-bold text-lg w-8 text-center">{item.quantity}</span>
                    <button
                      onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white font-bold"
                    >
                      +
                    </button>
                  </div>

                  <div className="text-right">
                    <p className="font-bold text-blue-600">
                      ₹{(item.price * item.quantity).toFixed(2)}
                    </p>
                    <button
                      onClick={() => setModifiedItems(prev => prev.filter(i => i.id !== item.id))}
                      className="text-red-600 hover:text-red-800 text-sm font-bold mt-1"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* ADD NEW ITEM FORM */}
            {showAddItem && (
              <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50 space-y-3">
                <h4 className="font-bold text-gray-800">➕ Add New Item</h4>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Item Name</label>
                  <input
                    type="text"
                    placeholder="e.g., Extra Paneer, Dessert"
                    value={newItem?.name || ""}
                    onChange={(e) => setNewItem({ 
                      ...newItem!, 
                      name: e.target.value 
                    })}
                    className="w-full border rounded px-3 py-2 text-sm mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Price (₹)</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={newItem?.price || ""}
                    onChange={(e) => setNewItem({ 
                      ...newItem!, 
                      price: parseFloat(e.target.value) || 0 
                    })}
                    className="w-full border rounded px-3 py-2 text-sm mt-1"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Quantity</label>
                  <input
                    type="number"
                    value={newItem?.quantity || 1}
                    onChange={(e) => setNewItem({ 
                      ...newItem!, 
                      quantity: parseInt(e.target.value) || 1 
                    })}
                    className="w-full border rounded px-3 py-2 text-sm mt-1"
                    min="1"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleAddNewItem}
                    className="flex-1 bg-blue-600 text-white py-2 rounded font-semibold hover:bg-blue-700"
                  >
                    Add Item
                  </button>
                  <button
                    onClick={() => {
                      setShowAddItem(false);
                      setNewItem(null);
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded font-semibold hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* ADD ITEM BUTTON */}
            {!showAddItem && (
              <button
                onClick={() => {
                  setShowAddItem(true);
                  setNewItem({ name: "", price: 0, quantity: 1 });
                }}
                className="w-full border-2 border-dashed border-blue-400 rounded-lg p-3 text-blue-600 font-semibold hover:bg-blue-50 mb-4"
              >
                + Add Another Item
              </button>
            )}

            {/* Price Summary */}
            <div className="mt-6 bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Original Total:</span>
                  <span className="font-semibold">₹{originalTotal.toFixed(2)}</span>
                </div>
                
                {calculation.difference !== 0 && (
                  <div className={`flex justify-between text-sm font-bold ${
                    calculation.difference < 0 ? 'text-green-600' : 'text-orange-600'
                  }`}>
                    <span>{calculation.difference < 0 ? 'Refund:' : 'Additional Payment:'}</span>
                    <span>{calculation.difference < 0 ? '-' : '+'}₹{Math.abs(calculation.difference).toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-blue-300">
                  <span>New Total:</span>
                  <span className="text-orange-600">₹{newTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* Refund Notice */}
              {calculation.refundAmount > 0 && (
                <div className="mt-3 bg-green-100 border border-green-300 rounded p-3">
                  <p className="text-sm text-green-800">
                    💰 <strong>₹{calculation.refundAmount.toFixed(2)}</strong> will be refunded to your wallet immediately.
                  </p>
                </div>
              )}

              {/* Additional Payment */}
              {calculation.paymentRequired && (
                <div className="mt-3 bg-orange-100 border border-orange-300 rounded p-3">
                                    <p className="text-sm text-orange-800 font-semibold mb-2">
                    Payment Required: ₹{calculation.difference.toFixed(2)}
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="payment"
                        value="wallet"
                        checked={paymentMethod === 'wallet'}
                        onChange={() => setPaymentMethod('wallet')}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Pay from Wallet</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="payment"
                        value="upi"
                        checked={paymentMethod === 'upi'}
                        onChange={() => setPaymentMethod('upi')}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Pay via UPI</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="payment"
                        value="food"
                        checked={paymentMethod === 'food'}
                        onChange={() => setPaymentMethod('food')}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">FOOD (Pay on Delivery)</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex gap-3">
          <Button
            variant="secondary"
            onClick={onClose}
            className="flex-1"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            className="flex-1"
            isLoading={loading}
            disabled={calculation.difference === 0}
          >
            {calculation.refundAmount > 0 
              ? `Confirm & Get Refund` 
              : calculation.paymentRequired
                ? `Confirm & Pay`
                : 'Confirm Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

